import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Info, Music2, Video } from "lucide-react";
import { useNavigate } from "react-router-dom";

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1519681393784-d120267933ba?w=2000&q=85",
  "https://images.unsplash.com/photo-1465101162946-4377e57745c3?w=2000&q=85",
  "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=2000&q=85",
  "https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=2000&q=85",
  "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=2000&q=85",
];

export default function MediaHero({ items }) {
  const [idx, setIdx] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    if (!items || items.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 7000);
    return () => clearInterval(t);
  }, [items]);

  if (!items || items.length === 0) return null;
  const item = items[idx];
  const bg = HERO_IMAGES[idx % HERO_IMAGES.length];

  return (
    <section className="relative h-[85vh] min-h-[560px] overflow-hidden" data-testid="media-hero">
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <img src={bg} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-gradient" />
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 h-full flex flex-col justify-end px-4 md:px-8 lg:px-12 pb-16 md:pb-24 max-w-4xl">
        <motion.div
          key={`meta-${idx}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="live-dot" />
            <span className="text-xs tracking-[0.3em] uppercase text-[#D1CFC9] font-bold">
              {item.IsAudio ? "Áudio em Destaque" : "Vídeo em Destaque"}
            </span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] mb-4">
            {item.Descricao}
          </h1>
          <p className="text-slate-300 text-sm sm:text-base md:text-lg max-w-2xl mb-8 leading-relaxed">
            Conteúdo exclusivo do Media Center IHTP. Disponível para a sua subscrição ativa — reproduza,
            marque como favorito ou adicione a uma playlist.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => nav(`/media/watch/${item.ID_ConteudoMediaCenter}`)}
              className="media-btn-primary inline-flex items-center gap-2"
              data-testid="media-hero-play"
            >
              <Play size={18} fill="currentColor" /> Reproduzir agora
            </button>
            <button
              onClick={() => nav(`/media/watch/${item.ID_ConteudoMediaCenter}`)}
              className="media-btn-ghost inline-flex items-center gap-2"
              data-testid="media-hero-info"
            >
              <Info size={18} /> Mais info
            </button>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300">
              {item.IsAudio ? <Music2 size={12} /> : <Video size={12} />}
              {item.IsAudio ? "Áudio" : "Vídeo HD"}
            </div>
          </div>
        </motion.div>

        {items.length > 1 && (
          <div className="flex gap-2 mt-10" data-testid="media-hero-dots">
            {items.map((it, i) => (
              <button
                key={it.ID_ConteudoMediaCenter ?? `dot-${i}`}
                onClick={() => setIdx(i)}
                className={`h-1 rounded-full transition-all ${i === idx ? "w-8 bg-[#D1CFC9]" : "w-4 bg-white/20 hover:bg-white/40"}`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
